// =============================================================================
// features/crm/components/CrmQuickFilters.tsx — Chips de filtro rápido do
// CRM/Kanban (F33-S07, doc 26 §6.2): "Meus leads", "Retorno hoje",
// "Atrasados" e "Campanha".
//
// Presentational puro — toda a resolução de dado (quem sou eu, quais leads
// têm retorno hoje/atrasado) vive em hooks/crm/useLeadAppointments.ts e no
// componente pai (CrmListPage), que também é quem decide QUANDO este chip
// aparece (gate de permissão + flag — nunca calculado aqui).
//
// Seleção única (não são filtros combináveis): ativar um chip desativa o
// anterior. Estado vem de fora (URL, via searchParams) — DoD do slot
// "filtros rápidos refletidos na URL e preservados ao voltar do detalhe".
// =============================================================================

import * as React from 'react';

import { cn } from '../../../lib/cn';

export type CrmQuickFilterKey = 'mine' | 'today' | 'overdue' | 'campaign';

export interface CrmQuickFiltersProps {
  active: CrmQuickFilterKey | null;
  onChange: (key: CrmQuickFilterKey | null) => void;
  /** "Meus leads" só é clicável quando o agente do usuário atual foi resolvido. */
  mineAvailable: boolean;
  /** "Campanha" só aparece com `sales.campaigns.enabled` — F35, fora deste slot. */
  showCampaign: boolean;
  isLoading?: boolean | undefined;
}

interface ChipDef {
  key: CrmQuickFilterKey;
  label: string;
  disabled?: boolean;
  title?: string | undefined;
}

function QuickFilterChip({
  def,
  active,
  onChange,
  isLoading,
}: {
  def: ChipDef;
  active: CrmQuickFilterKey | null;
  onChange: (key: CrmQuickFilterKey | null) => void;
  isLoading?: boolean | undefined;
}): React.JSX.Element {
  const isActive = active === def.key;
  return (
    <button
      type="button"
      aria-pressed={isActive}
      disabled={def.disabled}
      title={def.title}
      onClick={() => onChange(isActive ? null : def.key)}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill',
        'font-sans text-xs font-semibold',
        'border transition-all duration-fast ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        isActive
          ? 'bg-azul/10 border-azul text-azul shadow-e1'
          : 'bg-[var(--bg-elev-1)] border-border-subtle text-ink-3 hover:text-ink hover:border-border-strong',
      )}
    >
      {isActive && (
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          className="w-3 h-3 shrink-0"
          aria-hidden="true"
        >
          <path d="M3 8.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {def.label}
      {isActive && isLoading && (
        <span
          className="w-2.5 h-2.5 rounded-pill border-2 border-azul/30 border-t-azul animate-spin shrink-0"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

/**
 * Barra de chips de filtro rápido — F33-S07.
 * Renderizada pelo chamador (CrmListPage) só quando o gate de
 * permissão/flag de compromissos está aberto.
 */
export function CrmQuickFilters({
  active,
  onChange,
  mineAvailable,
  showCampaign,
  isLoading = false,
}: CrmQuickFiltersProps): React.JSX.Element {
  const chips: ChipDef[] = [
    {
      key: 'mine',
      label: 'Meus leads',
      disabled: !mineAvailable,
      title: mineAvailable ? undefined : 'Não foi possível identificar seu agente.',
    },
    { key: 'today', label: 'Retorno hoje' },
    { key: 'overdue', label: 'Atrasados' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtros rápidos">
      {chips.map((def) => (
        <QuickFilterChip
          key={def.key}
          def={def}
          active={active}
          onChange={onChange}
          isLoading={def.key !== 'mine' ? isLoading : false}
        />
      ))}

      {showCampaign && (
        <QuickFilterChip
          def={{
            key: 'campaign',
            label: 'Campanha',
            disabled: true,
            title: 'Filtragem por campanha chega com o módulo de campanhas (F35).',
          }}
          active={active}
          onChange={onChange}
        />
      )}
    </div>
  );
}
